export const EMPTY_NUMERIC_DISPLAY = '-'

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isEmptyDisplayValue(value) {
  return value === null || value === undefined || value === ''
}

/**
 * 数値をカンマ区切りでフォーマット
 *
 * @param {number | string} num
 * @returns {string}
 */
export function formatNumber(num) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * テーブル表示用の数値フォーマット（null/空は "-"）
 *
 * @param {number | string | null | undefined} value
 * @returns {string}
 */
export function formatDisplayNumber(value) {
  if (isEmptyDisplayValue(value)) {
    return EMPTY_NUMERIC_DISPLAY
  }
  const num = Number(value)
  if (Number.isNaN(num)) {
    return EMPTY_NUMERIC_DISPLAY
  }
  return formatNumber(num)
}

/**
 * テーブル表示用の数値・比率フォーマット（null/空は "-"）
 *
 * @param {number | string | null | undefined} value
 * @returns {string}
 */
export function formatDisplayValue(value) {
  if (isEmptyDisplayValue(value)) {
    return EMPTY_NUMERIC_DISPLAY
  }
  return String(value)
}

/**
 * 従業員数の表示用文字列（本体 (臨時) 形式）
 *
 * @param {number | string | null | undefined} regular
 * @param {number | string | null | undefined} temporary
 * @returns {string}
 */
export function formatEmployeeCountDisplay(regular, temporary) {
  return `${formatDisplayNumber(regular)} (${formatDisplayNumber(temporary)})`
}

/**
 * 11212「提出会社の状況に含める」チェック横の従業員数表示（正規のみ） 未登録・集計未実施時は ( - )
 *
 * @param {number | string | null | undefined} regular
 * @returns {string}
 */
export function formatIncludeCheckboxEmployeeCount(regular) {
  if (isEmptyDisplayValue(regular)) {
    return '( - )'
  }
  return `(${formatDisplayNumber(regular)})`
}

/**
 * パーセント値の表示用フォーマット（小数第1位固定、null/空は "-"）
 *
 * @param {number | string | null | undefined} value
 * @param {number} [decimalPlaces=1] Default is `1`
 * @returns {string}
 */
export function formatPercentValue(value, decimalPlaces = 1) {
  if (isEmptyDisplayValue(value)) {
    return EMPTY_NUMERIC_DISPLAY
  }
  const normalized = String(value).replace(/%$/, '').trim()
  if (normalized === EMPTY_NUMERIC_DISPLAY) {
    return EMPTY_NUMERIC_DISPLAY
  }
  const num = Number(normalized)
  if (Number.isNaN(num)) {
    return EMPTY_NUMERIC_DISPLAY
  }
  return num.toFixed(decimalPlaces)
}

/**
 * 増減率の表示用フォーマット（小数第1位固定）
 *
 * @param {string | number | null | undefined} rate
 * @returns {string}
 */
export function formatRate(rate) {
  return formatPercentValue(rate)
}

/**
 * 「##,### (#,###)」形式の文字列を {main, temp} に分解
 *
 * @param {string} countStr
 * @returns {{ main: string; temp: string }}
 */
export function splitEmployeeCount(countStr) {
  const m = (countStr || '').match(/^([\d,-]+)\s*\(([\d,-]+)\)$/)
  if (m) return { main: m[1], temp: m[2] }
  return { main: countStr || '', temp: '' }
}

/**
 * 給与を表示単位に応じてフォーマット
 *
 * @param {number | null | undefined} salaryYen
 * @param {'yen' | 'thousand-yen'} unit
 * @returns {string}
 */
export function formatSalary(salaryYen, unit) {
  if (isEmptyDisplayValue(salaryYen)) {
    return EMPTY_NUMERIC_DISPLAY
  }
  const num = Number(salaryYen)
  if (Number.isNaN(num)) {
    return EMPTY_NUMERIC_DISPLAY
  }
  if (unit === 'yen') {
    return formatNumber(num)
  }
  return formatNumber(Math.floor(num / 1000))
}

/**
 * エクスポート用ファイル名の年度部分をゼロパディング
 *
 * @param {string} yearLabel 例: 2024年3月期
 * @returns {string}
 */
export function formatExportYearLabel(yearLabel) {
  return yearLabel.replace(/(\d{4})年(\d{1,2})月期/, (_, y, m) => `${y}年${m.padStart(2, '0')}月期`)
}
